import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  InputAdornment,
  Alert,
  Divider,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from '@mui/material';
import {
  Speed as SpeedIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { Planos, PlanosGrupo } from '../../types';
import { planosService } from '../../services/planosService';
import { planosGruposService } from '../../services/planosGruposService';
import EscritorioSelect from '../selects/EscritorioSelect';

interface PlanosFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  plano?: Planos | null;
}

interface PlanosFormData {
  nome: string;
  descricao: string;
  grupo_id: number | '';
  velocidade_download: number;
  velocidade_upload: number;
  valor: number;
  ativo: boolean;
  escritorio_id: number[];
  pool_name: string;
  ativarBurstUp: boolean;
  ativarBurstDown: boolean;
  burstLimitUp: number;
  burstThresholdUp: number;
  burstTimeUp: number;
  burstLimitDown: number;
  burstThresholdDown: number;
  burstTimeDown: number;
}

const PlanosForm: React.FC<PlanosFormProps> = ({ open, onClose, onSuccess, plano }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [valorFormatado, setValorFormatado] = useState('0,00');
  const [grupos, setGrupos] = useState<PlanosGrupo[]>([]);

  const isEdit = !!plano;

  // Função para formatar valor para moeda brasileira
  const formatarMoeda = (valor: number): string => {
    return valor.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Função para converter string formatada para número
  const parsearMoeda = (valorFormatado: string): number => {
    // Remove todos os caracteres que não são números, vírgula ou ponto
    const numeroLimpo = valorFormatado.replace(/[^\d,]/g, '');
    // Substitui vírgula por ponto para conversão
    const numeroComPonto = numeroLimpo.replace(',', '.');
    const numero = parseFloat(numeroComPonto) || 0;
    return numero;
  };

  // Função para aplicar máscara de moeda durante a digitação
  const aplicarMascaraMoeda = (valor: string): string => {
    // Remove tudo que não é número
    const apenasNumeros = valor.replace(/\D/g, '');
    
    if (apenasNumeros === '') return '0,00';
    
    // Converte para número e divide por 100 para considerar os centavos
    const numero = parseInt(apenasNumeros) / 100;
    
    // Formata como moeda brasileira
    return numero.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PlanosFormData>({
    defaultValues: {
      nome: '',
      descricao: '',
      grupo_id: '',
      velocidade_download: 100,
      velocidade_upload: 50,
      valor: 0,
      ativo: true,
      escritorio_id: [],
      pool_name: '',
      ativarBurstUp: false,
      ativarBurstDown: false,
      burstLimitUp: 0,
      burstThresholdUp: 0,
      burstTimeUp: 0,
      burstLimitDown: 0,
      burstThresholdDown: 0,
      burstTimeDown: 0,
    },
  });

  // Função auxiliar para normalizar escritorio_id de diferentes formatos
  const normalizeEscritorioIds = (data: any): number[] => {
    if (data === null || data === undefined || (typeof data === 'string' && data.trim() === '')) {
      return [];
    }
    if (Array.isArray(data)) {
      return data.map(id => typeof id === 'number' ? id : parseInt(String(id), 10)).filter(id => !isNaN(id) && id > 0);
    }
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed)) {
          return parsed.map(id => typeof id === 'number' ? id : parseInt(String(id), 10)).filter(id => !isNaN(id) && id > 0);
        }
        if (typeof parsed === 'number' && !isNaN(parsed) && parsed > 0) {
          return [parsed];
        }
      } catch (e) {
        const numericValue = parseInt(data, 10);
        if (!isNaN(numericValue) && numericValue > 0) {
          return [numericValue];
        }
      }
    }
    if (typeof data === 'number' && !isNaN(data) && data > 0) {
      return [data];
    }
    return [];
  };

  useEffect(() => {
    if (open) {
      planosGruposService.getAll().then((response) => {
        if (response.success && response.data) {
          setGrupos(response.data);
        }
      }).catch(() => {
        setError('Erro ao carregar grupos de planos');
      });
    }
  }, [open]);

  useEffect(() => {
    if (!plano && open && grupos.length > 0) {
      setValue('grupo_id', grupos[0].id);
    }
  }, [plano, open, grupos, setValue]);

  useEffect(() => {
    if (plano) {
      reset({
        nome: plano.nome,
        descricao: plano.descricao || '',
        grupo_id: plano.grupo_id,
        velocidade_download: plano.velocidade_download,
        velocidade_upload: plano.velocidade_upload,
        valor: plano.valor,
        ativo: plano.ativo,
        escritorio_id: normalizeEscritorioIds(plano.escritorio_id),
        pool_name: plano.pool_name || '',
        ativarBurstUp: (plano as any).ativarBurstUp || false,
        ativarBurstDown: (plano as any).ativarBurstDown || false,
        burstLimitUp: (plano as any).burstLimitUp || 0,
        burstThresholdUp: (plano as any).burstThresholdUp || 0,
        burstTimeUp: (plano as any).burstTimeUp || 0,
        burstLimitDown: (plano as any).burstLimitDown || 0,
        burstThresholdDown: (plano as any).burstThresholdDown || 0,
        burstTimeDown: (plano as any).burstTimeDown || 0,
      });
      setValorFormatado(formatarMoeda(plano.valor));
    } else if (open) {
      reset({
        nome: '',
        descricao: '',
        grupo_id: grupos.length > 0 ? grupos[0].id : '',
        velocidade_download: 100,
        velocidade_upload: 50,
        valor: 0,
        ativo: true,
        escritorio_id: [],
        pool_name: '',
        ativarBurstUp: false,
        ativarBurstDown: false,
        burstLimitUp: 0,
        burstThresholdUp: 0,
        burstTimeUp: 0,
        burstLimitDown: 0,
        burstThresholdDown: 0,
        burstTimeDown: 0,
      });
      setValorFormatado('0,00');
    }
  }, [plano, open, reset]);

  // Handler para mudanças no campo valor com formatação de moeda
  const handleValorChange = (valor: string) => {
    const valorFormatado = aplicarMascaraMoeda(valor);
    const valorNumerico = parsearMoeda(valorFormatado);
    
    setValorFormatado(valorFormatado);
    setValue('valor', valorNumerico);
  };

  const onSubmit = handleSubmit((data) => {
    const submitData = async () => {
      setLoading(true);
      setError(null);

      const payload: Partial<Planos> = {
        ...data,
        grupo_id: Number(data.grupo_id),
      };

      try {
        if (isEdit && plano) {
          await planosService.update(plano.id, payload);
        } else {
          await planosService.create(payload);
        }
        onSuccess();
        onClose();
      } catch (err: any) {
        console.error('Erro ao salvar plano:', err);
        
        // Tratar erros de validação do backend
        if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
          const errorMessages = err.response.data.errors.join('\n');
          setError(errorMessages);
        } else {
          setError(err.response?.data?.message || 'Erro ao salvar plano');
        }
      } finally {
        setLoading(false);
      }
    };

    submitData();
  });

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setValorFormatado('0,00');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SpeedIcon color="primary" />
          <Typography variant="h6">
            {isEdit ? 'Editar Plano' : 'Novo Plano'}
          </Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={onSubmit}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Informações Básicas */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Informações Básicas
              </Typography>
            </Grid>

                         <Grid item xs={12} sm={6}>
               <Controller
                 name="nome"
                 control={control}
                 rules={{
                   required: 'Nome do plano é obrigatório',
                   minLength: { value: 2, message: 'Nome deve ter pelo menos 2 caracteres' },
                   maxLength: { value: 100, message: 'Nome deve ter no máximo 100 caracteres' }
                 }}
                 render={({ field }) => (
                   <TextField
                     {...field}
                     label="Nome do Plano"
                     size="small"
                     fullWidth
                     error={!!errors.nome}
                     helperText={errors.nome?.message}
                     placeholder="Ex: Plano Premium"
                   />
                 )}
               />
             </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="grupo_id"
                control={control}
                rules={{
                  required: 'Grupo é obrigatório',
                  validate: (value) => (value !== '' && Number(value) > 0) || 'Grupo é obrigatório',
                }}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.grupo_id}>
                    <InputLabel>Grupo</InputLabel>
                    <Select
                      {...field}
                      label="Grupo"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      {grupos.map((grupo) => (
                        <MenuItem key={grupo.id} value={grupo.id}>
                          {grupo.titulo}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.grupo_id && (
                      <FormHelperText>{errors.grupo_id.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

                         <Grid item xs={12} sm={3}>
               <Controller
                 name="valor"
                 control={control}
                 rules={{
                   required: 'Preço é obrigatório',
                   min: { value: 0, message: 'Preço deve ser maior ou igual a zero' }
                 }}
                 render={({ field }) => (
                   <TextField
                     label="Preço Mensal"
                     fullWidth
                     size="small"
                     error={!!errors.valor}
                     helperText={errors.valor?.message}
                     value={valorFormatado}
                     onChange={(e) => handleValorChange(e.target.value)}
                     InputProps={{
                       startAdornment: (
                         <InputAdornment position="start">R$</InputAdornment>
                       )                      
                     }}
                     placeholder="0,00"
                   />
                 )}
               />
             </Grid>

            <Grid item xs={12} sm={3}>
              <Controller
                name="ativo"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Plano Ativo"
                    sx={{ mt: 1 }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="descricao"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Descrição"
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    error={!!errors.descricao}
                    helperText={errors.descricao?.message}
                    placeholder="Descrição detalhada do plano..."
                  />
                )}
              />
            </Grid>

            {/* Velocidades */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Velocidades
              </Typography>
            </Grid>

            {/* Velocidade Download com Burst */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="velocidade_download"
                control={control}
                rules={{
                  required: 'Velocidade de download é obrigatória',
                  min: { value: 1, message: 'Velocidade deve ser no mínimo 1 Mbps' },
                  max: { value: 10000, message: 'Velocidade deve ser no máximo 10000 Mbps' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Velocidade de Download"
                    type="number"
                    size="small"
                    fullWidth
                    error={!!errors.velocidade_download}
                    helperText={errors.velocidade_download?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SpeedIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">Mbps</InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              {/* Burst Download */}
              <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Controller
                  name="ativarBurstDown"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label="Ativar Burst Download"
                      sx={{ mb: 1 }}
                    />
                  )}
                />
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Controller
                      name="burstLimitDown"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Burst Limit (Mbps)"
                          type="number"
                          size="small"
                          fullWidth
                          error={!!errors.burstLimitDown}
                          helperText={errors.burstLimitDown?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SpeedIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                          disabled={!watch('ativarBurstDown')}
                        />
                      )}
                    />
                  </Grid>                  
                  <Grid item xs={12}>
                    <Controller
                      name="burstThresholdDown"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Burst Threshold (Mbps)"
                          type="number"
                          size="small"
                          fullWidth
                          error={!!errors.burstThresholdDown}
                          helperText={errors.burstThresholdDown?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SpeedIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                          disabled={!watch('ativarBurstDown')}
                        />
                      )}
                    />
                  </Grid>                  
                  <Grid item xs={12}>
                    <Controller
                      name="burstTimeDown"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Burst Time (segundos)"
                          type="number"
                          size="small"
                          fullWidth
                          error={!!errors.burstTimeDown}
                          helperText={errors.burstTimeDown?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SpeedIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">s</InputAdornment>
                            ),
                          }}
                          disabled={!watch('ativarBurstDown')}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Velocidade Upload com Burst */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="velocidade_upload"
                control={control}
                rules={{
                  required: 'Velocidade de upload é obrigatória',
                  min: { value: 1, message: 'Velocidade deve ser no mínimo 1 Mbps' },
                  max: { value: 10000, message: 'Velocidade deve ser no máximo 10000 Mbps' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Velocidade de Upload"
                    type="number"
                    size="small"
                    fullWidth
                    error={!!errors.velocidade_upload}
                    helperText={errors.velocidade_upload?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SpeedIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">Mbps</InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              {/* Burst Upload */}
              <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Controller
                  name="ativarBurstUp"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label="Ativar Burst Upload"
                      sx={{ mb: 1 }}
                    />
                  )}
                />
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Controller
                      name="burstLimitUp"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Burst Limit (Mbps)"
                          type="number"
                          size="small"
                          fullWidth
                          error={!!errors.burstLimitUp}
                          helperText={errors.burstLimitUp?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SpeedIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                          disabled={!watch('ativarBurstUp')}
                        />
                      )}
                    />
                  </Grid>                  
                  <Grid item xs={12}>
                    <Controller
                      name="burstThresholdUp"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Burst Threshold (Mbps)"
                          type="number"
                          size="small"
                          fullWidth
                          error={!!errors.burstThresholdUp}
                          helperText={errors.burstThresholdUp?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SpeedIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                          disabled={!watch('ativarBurstUp')}
                        />
                      )}
                    />
                  </Grid>                  
                  <Grid item xs={12}>
                    <Controller
                      name="burstTimeUp"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Burst Time (segundos)"
                          type="number"
                          size="small"
                          fullWidth
                          error={!!errors.burstTimeUp}
                          helperText={errors.burstTimeUp?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SpeedIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">s</InputAdornment>
                            ),
                          }}
                          disabled={!watch('ativarBurstUp')}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Pool de Rede */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="pool_name"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="pool-name-label">Pool Name</InputLabel>
                    <Select
                      labelId="pool-name-label"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      label="Pool Name"
                      size="small"
                    > 
                      <MenuItem value="">
                        <em>Selecione um pool</em>
                      </MenuItem>
                      <MenuItem value="pool_cliente">pool_cliente</MenuItem>
                      <MenuItem value="pool_cgnat">pool_cgnat</MenuItem>
                    </Select>
                    <FormHelperText>Pool utilizado para endereçamento IP</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>            

            {/* Escritórios */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                <Box display="flex" alignItems="center" gap={1}>
                  <BusinessIcon />
                  Escritórios Autorizados
                </Box>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Selecione os escritórios onde este plano estará disponível
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="escritorio_id"
                control={control}
                rules={{
                  required: 'Pelo menos um escritório deve ser selecionado',
                  validate: {
                    notEmpty: (value) => {
                      if (!Array.isArray(value) || value.length === 0) {
                        return 'Pelo menos um escritório deve ser selecionado';
                      }
                      return true;
                    }
                  }
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <EscritorioSelect
                    value={value || []}
                    onChange={(selectedEscritorioIds) => {
                      onChange(selectedEscritorioIds);
                    }}
                    size="small"
                    label="Escritórios *"
                    placeholder="Selecione os escritórios..."
                    multiple={true}
                    onlyActive={true}
                    allowClear={false}
                    required={true}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? 'Salvando...' : isEdit ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PlanosForm; 